import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { getIsWhiteLabel, getWhiteLabelPrimaryColor } from '@/redux/modules/whiteLabel';
import {
    getUiForgotPasswordError,
    getUiForgotPasswordErrorMessage,
    getUiForgotPasswordSubmitted,
} from '@/redux/modules/forgotPassword';
import { ModalActions } from '@liveauctioneers/hammer-ui-core/modal/components';
import { required, email as validateEmail } from '@/utils/validation';
import { useAppSelector } from '@/redux/hooks';
import appTheme from '@liveauctioneers/caterwaul-components/lib/theme/theme';
import Checkbox from '@liveauctioneers/caterwaul-components/lib/Checkbox/Checkbox';
import Input from '@liveauctioneers/caterwaul-components/lib/Input/Input';
import React, { useState } from 'react';
import styled, { css } from 'styled-components';

type Props = {
    submitForgotPassword: (email: string, sendSMS: boolean) => void;
};

const ForgotPassword = ({ submitForgotPassword }: Props) => {
    const { formatMessage } = useIntl();
    const [email, setEmail] = useState<string>('');
    const [emailValid, setEmailValid] = useState<boolean>(true);
    const [sendSMS, setSendSMS] = useState<boolean>(false);

    const isWhiteLabel = useAppSelector(getIsWhiteLabel);
    const whiteLabelPrimaryColor = useAppSelector(getWhiteLabelPrimaryColor);
    const forgotPasswordSubmitted = useAppSelector(getUiForgotPasswordSubmitted);
    const forgotPasswordError = useAppSelector(getUiForgotPasswordError);
    const forgotPasswordErrorMessage = useAppSelector(getUiForgotPasswordErrorMessage);

    const onEmailChange = (event: any) => {
        setEmail(event.target.value);
    };

    const onSubmit = () => {
        const trimmedEmail = email.trim();
        const newEmailValid = required(trimmedEmail) && validateEmail(trimmedEmail);
        setEmailValid(newEmailValid);

        if (newEmailValid) {
            submitForgotPassword(trimmedEmail, sendSMS);
            setEmail(trimmedEmail);
        }
    };

    const handleEnterKeyPress = (event: any) => {
        if (event.key === 'Enter') {
            onSubmit();
        }
        event.stopPropagation();
    };

    return (
        <>
            <Input
                aria-label={formatMessage({
                    id: 'forgotPassword.emailAddress',
                })}
                autoComplete="email"
                errorMessage={forgotPasswordErrorMessage || <FormattedMessage id="forgotPassword.error" />}
                id="email"
                label={formatMessage({ id: 'forgotPassword.emailAddress' })}
                onChange={onEmailChange}
                onKeyPress={handleEnterKeyPress}
                testid="forgotPasswordModalEmail"
                type="text"
                valid={!forgotPasswordError && emailValid}
            />
            <StyledCheckbox
                $addMarginTop={forgotPasswordError && emailValid}
                checked={sendSMS}
                label={formatMessage({
                    id: 'forgotPassword.sendResetLinkToSMS',
                })}
                name="sendSMSCheckbox"
                onClick={() => setSendSMS(!sendSMS)}
            />
            <ModalActions usePortalActions>
                <Button
                    data-testid="forgotPasswordModalSendReset"
                    disabled={forgotPasswordSubmitted}
                    loading={forgotPasswordSubmitted}
                    onClick={onSubmit}
                    style={!isWhiteLabel || whiteLabelPrimaryColor === appTheme.colors.blue200 ? 'critical' : 'primary'}
                    variant="filled"
                >
                    {formatMessage({ id: 'forgotPassword.submit' })}
                </Button>
            </ModalActions>
        </>
    );
};

export default ForgotPassword;

const StyledCheckbox = styled(Checkbox)<{ $addMarginTop }>`
    ${({ $addMarginTop }) => {
        return css`
            margin-top: ${$addMarginTop && '32px'};
        `;
    }};
`;
